import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// 価格帯文字列からおおよその数値指標を推定
// 例: "¥¥" => 2, "¥1000-2000" => 1500, "2000-3000" => 2500, "¥3000" => 3000
export function estimatePriceValue(input?: string | null): number | null {
  if (!input) return null;
  const s = String(input).trim();
  if (!s) return null;
  // numeric range like 1000-2000 or ¥1000-¥2000
  const nums = s.match(/\d{3,6}/g);
  if (nums && nums.length > 0) {
    const values = nums.map((n) => parseInt(n, 10)).filter((n) => !isNaN(n));
    if (values.length === 1) return values[0];
    if (values.length >= 2) {
      const avg = (values[0] + values[1]) / 2;
      return Math.round(avg);
    }
  }
  // yen symbols count: ¥, ￥
  const yenCount = (s.match(/[¥￥]/g) || []).length;
  if (yenCount > 0) return yenCount; // 小さなスケールだが相対ソートに使える
  return null;
}

// --- Dropdown sort comparator (日本語ルール) ---
// 仕様:
// - ひらがな/カタカナのみ: 五十音順（ひら/カタ等価, numeric: false）
// - 都道府県名: 北→南の固定順
// - 数字のみ: NFKC 正規化後のコードポイント順
// - その他（漢字等を含む）: NFKC 正規化後のコードポイント順
// - 異種混在: 上記を順に試し、決まらなければ最後に日本語 Collator(base)

const PREF_ORDER = [
  "北海道","青森県","岩手県","宮城県","秋田県","山形県","福島県","茨城県","栃木県","群馬県","埼玉県","千葉県","東京都","神奈川県","新潟県","富山県","石川県","福井県","山梨県","長野県","岐阜県","静岡県","愛知県","三重県","滋賀県","京都府","大阪府","兵庫県","奈良県","和歌山県","鳥取県","島根県","岡山県","広島県","山口県","徳島県","香川県","愛媛県","高知県","福岡県","佐賀県","長崎県","熊本県","大分県","宮崎県","鹿児島県","沖縄県"
];
const PREF_INDEX: Record<string, number> = Object.fromEntries(PREF_ORDER.map((p, i) => [p, i]));
const PREF_ALIAS: Record<string, string> = Object.fromEntries(
  PREF_ORDER.flatMap((pref) => {
    const short = pref.replace(/[都道府県]$/, "");
    return [[pref, pref], [short, pref]];
  })
);

const JA_BASE = new Intl.Collator('ja', { sensitivity: 'base', usage: 'sort', numeric: false });
const KANA_ONLY_RE = /^[\u3040-\u309F\u30A0-\u30FF\u30FB\u30FC\s]+$/; // ひら/カタ/中黒/長音/空白
const DIGIT_ONLY_RE = /^[0-9]+$/;

function normalizeNFKC(s: string): string {
  try { return s.normalize('NFKC'); } catch { return s; }
}

export function normalizePrefectureLabel(input?: string | null): string {
  const raw = normalizeNFKC(String(input || '')).replace(/\s+/g, '').trim();
  if (!raw) return '';
  return PREF_ALIAS[raw] || raw;
}

export function normalizeMunicipalityLabel(input?: string | null): string {
  return normalizeNFKC(String(input || '')).replace(/\s+/g, '').trim();
}

function categoryOf(raw: string): 'pref' | 'kana' | 'digit' | 'other' {
  const s = normalizePrefectureLabel(raw) || normalizeNFKC(raw).trim();
  if (s in PREF_INDEX) return 'pref';
  if (s.length > 0 && KANA_ONLY_RE.test(s)) return 'kana';
  if (s.length > 0 && DIGIT_ONLY_RE.test(s)) return 'digit';
  return 'other';
}

function cmpCodePoint(a: string, b: string): number {
  const A = normalizeNFKC(a);
  const B = normalizeNFKC(b);
  if (A === B) return 0;
  return A < B ? -1 : 1; // UTF-16 コードユニット順（BMPではコードポイント順と一致）
}

export function compareDropdown(a: string, b: string): number {
  if (a === b) return 0;
  const ca = categoryOf(a);
  const cb = categoryOf(b);

  // 両方が都道府県
  if (ca === 'pref' && cb === 'pref') {
    return PREF_INDEX[normalizePrefectureLabel(a)] - PREF_INDEX[normalizePrefectureLabel(b)];
  }
  // 両方が仮名のみ
  if (ca === 'kana' && cb === 'kana') {
    return JA_BASE.compare(normalizeNFKC(a), normalizeNFKC(b));
  }
  // 両方が数字のみ
  if (ca === 'digit' && cb === 'digit') {
    return cmpCodePoint(a, b);
  }
  // 上記以外は文字コード順
  const cp = cmpCodePoint(a, b);
  if (cp !== 0) return cp;
  // 念のための最終フォールバック
  return JA_BASE.compare(a, b);
}

// --- Household users mapping (UID -> 表示名) ---
const UID_TO_NAME: Record<string, string> = {
  'dODXX4ZILFemGyBovRqDZDlvuBJ2': 'だい',
  'LLHgX8cjbWUqLnBAniXj6z62Tw52': 'ちー',
};

export function userNameFromUid(uid?: string): string | undefined {
  if (!uid) return undefined;
  return UID_TO_NAME[uid];
}

export function whoWants(wishlistBy?: string[]): string[] {
  if (!Array.isArray(wishlistBy)) return [];
  const names = wishlistBy.map((u) => userNameFromUid(u)).filter((v): v is string => !!v);
  // 重複排除
  return Array.from(new Set(names));
}
